import { createSlice } from '@reduxjs/toolkit'
import {
  setUserError,
  setUserIsError,
  setUserPending,
  setUserSuccess,
  setUserData,
  updateAccountData,
  updateUser,
} from '@wellesley/host-remote/api/actions'
import { type AccountWithUser } from '@wellesley/openapi'

type UserStateData = AccountWithUser | null

export type UserState = {
  data: UserStateData
  isPending: boolean
  isError: boolean
  isSuccess: boolean
  error?: unknown
}

export type StoreWithUser = {
  user: UserState
}

const initialState: UserState = {
  data: null,
  isError: false,
  isPending: false,
  isSuccess: false,
}

export const userSlice = createSlice({
  name: 'user',
  initialState,
  reducers: {
    reset: (state) => {
      Object.assign(state, initialState)
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(setUserData, (state, { payload }) => {
        state.data = payload
      })
      .addCase(updateAccountData, (state, { payload }) => {
        state.data && Object.assign(state.data, payload || {})
      })
      .addCase(setUserSuccess, (state, { payload }) => {
        state.isSuccess = payload
      })
      .addCase(setUserPending, (state, { payload }) => {
        state.isPending = payload
      })
      .addCase(setUserIsError, (state, { payload }) => {
        state.isError = payload
      })
      .addCase(setUserError, (state, { payload }) => {
        state.error = payload
      })
      .addCase(updateUser, (state, { payload }) => {
        if (state.data?.users) {
          state.data.users = state.data.users.map((u) =>
            payload.id === u.id ? payload : u,
          )
        }
      })
  },
  selectors: {
    selectIsError: (state) => state.isError,
    selectError: (state) => state.error,
    selectPending: (state) => state.isPending,
    selectData: (state) => state.data,
    selectAccount: (state) => state.data?.account,
    selectAccountId: (state) => state.data?.account.id,
    selectUsers: (state) => state.data?.users.filter(({ deleted }) => !deleted),
    selectAllUsers: (state) => state.data?.users,
    selectUserById: (state, userId: string) =>
      state.data?.users.find(({ id }) => id === userId),
    selectCurrentUser: (state) =>
      state.data?.users.find(({ id }) => id === state.data?.selectedUserId),
    selectSelectedUserId: (state) => state.data?.selectedUserId,
    selectSuccess: (state) => state.isSuccess,
  },
})
